import { Chain } from '@ankr.com/chains-list';
import { MouseEvent } from 'react';

import { ChainProtocolContext } from 'domains/chains/screens/ChainPage/constants/ChainProtocolContext';
import { ChainSelectorContent } from 'modules/common/components/ChainSelectorContent';
import { Dialog } from 'uiKit/Dialog';
import { Endpoints } from 'modules/common/components/GetStartedSection/components/Endpoints';
import { JWT } from 'domains/jwtToken/types';
import { getIsHiddenMainnet } from 'domains/projects/screens/ProjectPage/components/ProjectChainDetails/hooks/useProjectChainDetails';
import { isMultichain } from 'modules/chains/utils/isMultichain';
import { useChainItemHeaderContent } from 'domains/chains/screens/ChainPage/components/ChainItemHeader/hooks/useChainItemHeaderContent';
import { usePrivateChainItem } from 'domains/chains/screens/ChainPage/PrivateChainItemQuery/components/PrivateChainItem/hooks/usePrivateChainItem';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { TokenSelector } from './components/TokenSelector/TokenSelector';
import { endpointModalTranslation } from './translation';
import { useCopyEndpointModalStyles } from './useCopyEndpointModalStyles';

const onDialogClick = (event: MouseEvent) => event.stopPropagation();

interface ICopyEndpointModalProps {
  chain: Chain;
  currentProjectIndex: number;
  hasProjectSelector?: boolean;
  isOpened: boolean;
  isProtocolSwitcherHidden?: boolean;
  jwtTokens: JWT[];
  onClickCloseButton: () => void;
}

export const CopyEndpointModalContent = ({
  chain,
  currentProjectIndex,
  hasProjectSelector,
  isOpened,
  isProtocolSwitcherHidden = false,
  jwtTokens,
  onClickCloseButton,
}: ICopyEndpointModalProps) => {
  const {
    chainProtocolContext,
    chainSubTypeTab,
    chainSubTypeTabs,
    chainType,
    chainTypeTab,
    chainTypeTabs,
    group,
    groupID,
    groupTab,
    groupTabs,
    groups,
    selectGroup,
  } = usePrivateChainItem({
    chain,
    isGroupSelectorAutoWidth: true,
    isProtocolSwitcherHidden,
    isHiddenMainnet: getIsHiddenMainnet(chain),
    shouldMergeZkVerifyGroups: true,
  });

  const { isChainProtocolSwitchEnabled } = chainProtocolContext;

  const { endpointsGroup, placeholder } = useChainItemHeaderContent({
    group,
    isMultiChain: isMultichain(chain.id),
    chain,
    groupID,
    isChainProtocolSwitchEnabled,
  });

  const { keys, t } = useTranslation(endpointModalTranslation);

  const { classes } = useCopyEndpointModalStyles();

  return (
    <ChainProtocolContext.Provider value={chainProtocolContext}>
      <Dialog
        onClick={onDialogClick}
        onClose={onClickCloseButton}
        open={isOpened}
        paperClassName={classes.endpointsDialog}
        shouldStopPropagationOnClose
        title={t(keys.copyEndpoint)}
      >
        {hasProjectSelector && (
          <TokenSelector
            jwtTokens={jwtTokens}
            currentProjectIndex={currentProjectIndex}
          />
        )}
        <ChainSelectorContent
          className={classes.chainSelectorControls}
          chainSubTypeTab={chainSubTypeTab}
          chainSubTypeTabs={chainSubTypeTabs}
          chainTypeTab={chainTypeTab}
          chainTypeTabs={chainTypeTabs}
          groupID={groupID}
          groupTab={groupTab}
          groupTabs={groupTabs}
          groups={groups}
          hasGroupSelector={false}
          selectGroup={selectGroup}
          isGroupSelectorAutoWidth={false}
          isSubchainSelectorHidden={isMultichain(chain.id)}
        />
        <Endpoints
          publicChain={chain}
          chainType={chainType}
          group={endpointsGroup}
          placeholder={placeholder}
        />
      </Dialog>
    </ChainProtocolContext.Provider>
  );
};
